import React, { ReactElement, useCallback, useMemo } from 'react'
import { CopyDeepLinkButtonProps } from './CopyDeepLinkButton.types'
import { Button, message } from 'antd'
import { CopyOutlined } from '@ant-design/icons'
import {
    buildUniversalContentLinkSimple,
    ContentType,
} from 'common/src/utils/buildUniversalContentLink'
import { useRecoilValue } from 'recoil'
import { activeCourseConfigSelector } from '@/atoms/courseInfo'
import { hashContentPath } from 'common/src/ContentPath'
import { MessageKeys } from '@/utils/messageKeys'
import { reusableCssClass } from '@/utils/reusableCssClasses'

export const CopyDeepLinkButton: React.FC<CopyDeepLinkButtonProps> = (
    props
): ReactElement => {
    const courseConfig = useRecoilValue(activeCourseConfigSelector)

    const deepLinkUrl = useMemo((): string => {
        let linkId: string

        // Build the appropriate ID based on content type
        if (
            props.contentType === ContentType.OUTLINE ||
            props.contentType === ContentType.SUMMARY
        ) {
            if (!props.contentPath) {
                throw new Error(
                    'contentPath is required for outline and summary deep links'
                )
            }
            // For outlines and summaries, use hashed content path
            linkId = hashContentPath(props.contentPath)
        } else {
            if (!props.id) {
                throw new Error(
                    'id is required for notecard and practice problem deep links'
                )
            }
            linkId = props.id
        }

        // Build the full URL using the common utility
        const url = buildUniversalContentLinkSimple(
            {
                type: props.contentType,
                id: linkId,
            },
            courseConfig.type
        )

        return url
    }, [props.contentType, props.id, props.contentPath, courseConfig.type])

    const copyDeepLinkToClipboard = useCallback(
        (event: React.MouseEvent<HTMLElement>): void => {
            message.destroy(MessageKeys.COPY_DEEP_LINK)
            event.stopPropagation()
            navigator.clipboard.writeText(deepLinkUrl).then(() =>
                message.success({
                    content: `Successfully copied ${props.contentType} deep link to clipboard`,
                    duration: 3,
                    key: MessageKeys.COPY_DEEP_LINK,
                    className: reusableCssClass.clickMe,
                    onClick: () => message.destroy(MessageKeys.COPY_DEEP_LINK),
                })
            )
        },
        [deepLinkUrl, props.contentType]
    )

    const buttonText = useMemo((): string => {
        if (
            props.contentType === ContentType.OUTLINE ||
            props.contentType === ContentType.SUMMARY
        ) {
            return `Copy ${props.contentType} Link`
        }
        return 'Copy Deep Link'
    }, [props.contentType])

    return (
        <Button
            icon={<CopyOutlined />}
            onClick={copyDeepLinkToClipboard}
            title={`Copy website frontend ${props.contentType.toLowerCase()} deep link to clipboard`}
        >
            {buttonText}
        </Button>
    )
}
